import { Controller } from "@hotwired/stimulus"
import { setHidden, isHidden } from "../lib/display_toggle"
import { retoneStatusBadge, statusBadgeClass } from "../lib/status_badge"

// Lead Defaults completion checklist — the browser twin of
// LeadProfileChecklistPresenter (#13319). The server renders the pills for the
// first paint; this controller rebuilds them from the LIVE inputs so the card
// keeps agreeing with what the boxes show after user-edit copies the profile in
// (page load with "Use profile info for lead defaults" on, the toggle itself), a
// chip or keystroke changes the title, a value is cleared, a Display Photo is
// chosen or its crop cancelled — and never claims an unsaved value is saved.
//
// Per field, the `fields` value (presenter#field_configs) carries the input id,
// the normalized SAVED value, the normalized value the profile would supply,
// whether it inherits without the flag (the calendar's offered GOAT link) and
// the copy for every state, so the states resolve exactly as on the server:
//
//   blank on screen                     → missing (optional for the calendar)
//   equals the saved value              → present
//   equals the profile value (flag on)  → inherited
//   anything else                       → draft (filled, not saved)
//
// Photo: the preview box's data-display-photo-attached is the saved state, a
// chosen file is a draft, and user-edit's visible borrow note means the profile
// photo will be attached on save (inherited).
//
// Listens on the hosting form for `input`/`change` and for
// `lead-profile-checklist:refresh`, which user-edit dispatches after each write
// that fires no input event (profile copy, clear, disable, photo borrow).
//
// Wire: data-controller="lead-profile-checklist"
//       data-lead-profile-checklist-fields-value='[{field,label,inputId,optional,compare,saved,inherited,inheritsWithoutFlag,texts,pillTexts}]'
//       data-lead-profile-checklist-total-value="7"
//       targets: progress, progressIcon, progressText, pills, unsavedNote, unsavedNoteText
//
// Layout (#18893): the controller sits on the Lead Defaults card; `progress` is
// the header pill and `pills` is a Review List (<dl>) with one row per field —
// the label on the left, a state pill (pillTexts) on the right whose accessible
// name is the full sentence (texts).
export default class extends Controller {
  static targets = ["progress", "progressIcon", "progressText", "pills", "unsavedNote", "unsavedNoteText"]
  static values = { fields: Array, total: Number }

  static REFRESH_EVENT = "lead-profile-checklist:refresh"
  static FLAG_SELECTOR = "#defaults_same_as_profile"
  static PHOTO_PREVIEW_SELECTOR = '[data-user-edit-target="displayPhotoPreview"]'
  static PHOTO_BORROW_NOTE_SELECTOR = '[data-user-edit-target="displayPhotoBorrowNote"]'
  static COUNTED = new Set(["present", "inherited", "draft"])
  static UNSAVED = new Set(["inherited", "draft"])
  static BADGE_TONES = {
    present: "success",
    inherited: "info",
    draft: "warning",
    missing: "danger",
    optional: "secondary",
  }
  static ICON_CLASSES = {
    present: "ph-check-circle",
    inherited: "ph-user-circle",
    draft: "ph-pencil-simple-line",
    missing: "ph-x-circle",
    optional: "ph-minus-circle",
  }

  connect() {
    this.form = this.element.closest("form")
    this.boundRecompute = () => this.recompute()
    if (this.form) {
      this.form.addEventListener("input", this.boundRecompute)
      this.form.addEventListener("change", this.boundRecompute)
      this.form.addEventListener(this.constructor.REFRESH_EVENT, this.boundRecompute)
    }
    this.recompute()
  }

  disconnect() {
    if (!this.form || !this.boundRecompute) return
    this.form.removeEventListener("input", this.boundRecompute)
    this.form.removeEventListener("change", this.boundRecompute)
    this.form.removeEventListener(this.constructor.REFRESH_EVENT, this.boundRecompute)
  }

  recompute() {
    const rows = this.fieldsValue.map((field) => ({ ...field, state: this.resolve(field) }))
    this.renderPills(rows)
    this.renderProgress(rows)
  }

  // ── state resolution ────────────────────────────────────────────────────

  resolve(field) {
    if (field.field === "photo") return this.resolvePhoto()

    const input = document.getElementById(field.inputId)
    if (!input) return field.optional ? "optional" : "missing"

    const shown = this.normalize(field, input.value)
    if (!shown) return field.optional ? "optional" : "missing"
    if (shown === field.saved) return "present"
    if (field.inherited && shown === field.inherited && (field.inheritsWithoutFlag || this.profileFlagOn())) {
      return "inherited"
    }
    return "draft"
  }

  resolvePhoto() {
    const scope = this.form || document
    const preview = scope.querySelector(this.constructor.PHOTO_PREVIEW_SELECTOR)
    if (preview?.dataset.displayPhotoAttached === "true") return "present"
    const input = scope.querySelector("#user_display_photo")
    if ((input?.files?.length ?? 0) > 0) return "draft"
    const note = scope.querySelector(this.constructor.PHOTO_BORROW_NOTE_SELECTOR)
    if (note && !isHidden(note) && this.profileFlagOn()) return "inherited"
    return "missing"
  }

  profileFlagOn() {
    const flag = (this.form || document).querySelector(this.constructor.FLAG_SELECTOR)
    return !!flag?.checked
  }

  // Mirrors LeadProfileChecklistPresenter#normalize: digits for the NPN, digits
  // minus a leading US country code for the phone, otherwise trimmed,
  // whitespace-collapsed and case-insensitive.
  normalize(field, value) {
    const text = String(value ?? "")
    if (field.compare === "digits") return text.replace(/\D/g, "")
    if (field.compare === "phone") {
      const digits = text.replace(/\D/g, "")
      return digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits
    }
    return text.trim().toLowerCase().replace(/\s+/g, " ")
  }

  // ── rendering ───────────────────────────────────────────────────────────

  renderPills(rows) {
    if (!this.hasPillsTarget) return
    this.pillsTarget.replaceChildren(...rows.map((row) => this.buildRow(row)))
  }

  // One Review List row: <div class="gl-review-list__row"><dt>label</dt><dd>pill</dd></div>.
  buildRow(row) {
    const wrapper = document.createElement("div")
    wrapper.className = "gl-review-list__row"
    const label = document.createElement("dt")
    label.className = "gl-review-list__label"
    label.textContent = row.label
    const value = document.createElement("dd")
    value.className = "gl-review-list__value"
    value.append(this.buildPill(row))
    wrapper.append(label, value)
    return wrapper
  }

  buildPill(row) {
    const missing = row.state === "missing"
    const pill = document.createElement(missing ? "a" : "span")
    if (missing) pill.href = `#${row.inputId}`
    pill.className = statusBadgeClass(this.constructor.BADGE_TONES[row.state], { size: "sm" })
    pill.dataset.field = row.field
    pill.dataset.state = row.state
    if (row.optional) pill.dataset.optional = "true"
    pill.setAttribute("aria-label", row.texts[row.state])
    const icon = document.createElement("i")
    icon.className = `ph-fill ${this.constructor.ICON_CLASSES[row.state]}`
    icon.setAttribute("aria-hidden", "true")
    const pillText = row.pillTexts?.[row.state] ?? row.texts[row.state]
    pill.append(icon, document.createTextNode(` ${pillText}`))
    return pill
  }

  renderProgress(rows) {
    const expected = rows.filter((row) => !row.optional)
    const complete = expected.filter((row) => this.constructor.COUNTED.has(row.state)).length
    const unsaved = rows.filter((row) => this.constructor.UNSAVED.has(row.state)).length
    const allSaved = expected.every((row) => row.state === "present")
    const total = this.totalValue || expected.length

    if (this.hasProgressTarget) {
      const badge = this.progressTarget
      // Never blue (#18893): an unsaved count is the agent's move, so amber.
      retoneStatusBadge(badge, allSaved ? "success" : "warning")
      badge.dataset.complete = String(complete)
      badge.dataset.total = String(total)
      badge.dataset.unsaved = String(unsaved)
    }
    if (this.hasProgressIconTarget) {
      this.progressIconTarget.classList.toggle("ph-check-circle", allSaved)
      this.progressIconTarget.classList.toggle("ph-circle-half", !allSaved)
    }
    if (this.hasProgressTextTarget) {
      this.progressTextTarget.textContent = allSaved ? "Complete" : `${complete} of ${total} complete`
    }
    if (this.hasUnsavedNoteTarget) setHidden(this.unsavedNoteTarget, unsaved === 0)
    if (this.hasUnsavedNoteTextTarget) this.unsavedNoteTextTarget.textContent = this.unsavedNote(unsaved)
  }

  // Same copy as LeadProfileChecklistPresenter#unsaved_note.
  unsavedNote(unsaved) {
    if (unsaved === 0) return ""
    if (unsaved === 1) return "1 value is filled but not saved yet — Save Lead Defaults keeps it for new orders."
    return `${unsaved} values are filled but not saved yet — Save Lead Defaults keeps them for new orders.`
  }
}
